import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { cleanAadhaar } from '../utils/validation';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const AddTeacher = () => {
  const navigate = useNavigate();
  const [teacherData, setTeacherData] = useState({
    name: "",
    address: "",
    date_of_birth: "",
    gender: "",
    blood_group: "",
    mobile_number: "",
    aadhar_number: "",
    religion: "",
    caste: "",
    rci_number: "",
    rci_renewal_date: "",
    qualifications_details: "",
    category: "",
    email: "",
  });
  const [aadharError, setAadharError] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState('');
  const [actualUsername, setActualUsername] = useState('');

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 4000);
  };

  const [classAssignment, setClassAssignment] = useState({
    class: "",
    year: new Date().getFullYear().toString(),
    division: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Special handling for Aadhaar: allow typing with spaces, keep only digits, format in groups of 4
    if (name === 'aadhar_number') {
      const raw = String(value || '');
      const digits = raw.replace(/\D/g, '').slice(0, 12);
      const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
      setTeacherData({ ...teacherData, [name]: formatted });

      if (digits.length === 0) {
        setAadharError('');
      } else if (digits.length !== 12) {
        setAadharError('Aadhaar must be exactly 12 digits.');
      } else if (!/^[2-9]\d{11}$/.test(digits)) {
        setAadharError('Aadhaar must start with a digit between 2 and 9.');
      } else {
        setAadharError('');
      }
      return;
    }

    setTeacherData({ ...teacherData, [name]: value });
  };

  const handleClassAssignmentChange = (field, value) => {
    setClassAssignment({ ...classAssignment, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setErrors({});
      setIsSubmitting(true);
      // Name and email are mandatory.
      if (!teacherData.name || !teacherData.name.trim()) {
        showToast('Name is required.', 'error');
        setIsSubmitting(false);
        return;
      }

      if (!teacherData.email || !teacherData.email.includes('@')) {
        showToast('A valid email is required.', 'error');
        setIsSubmitting(false);
        return;
      }

      // If Aadhaar has been entered, block submit while inline validation is failing.
      if (aadharError) {
        showToast(aadharError, 'error');
        setIsSubmitting(false);
        return;
      }

      // Validate class/division: if a class is selected, division must be selected too
      if (classAssignment.class && !classAssignment.division) {
        showToast('Please select Division for the assigned class.', 'error');
        setIsSubmitting(false);
        return;
      }

      // Aadhaar must be provided
      const cleanedAadhaar = teacherData.aadhar_number ? String(cleanAadhaar(teacherData.aadhar_number)) : '';
      if (!cleanedAadhaar) {
        showToast('Aadhaar is required.', 'error');
        setIsSubmitting(false);
        return;
      }

      // Do NOT auto-fill or generate placeholder data. Only send values the user provided.
      const teacherDataWithAssignments = {
        name: teacherData.name.trim(),
        address: teacherData.address?.trim() || null,
        date_of_birth: teacherData.date_of_birth || null,
        gender: teacherData.gender || null,
        blood_group: teacherData.blood_group || null,
        mobile_number: teacherData.mobile_number?.trim() || null,
        aadhar_number: cleanedAadhaar,
        religion: teacherData.religion || null,
        caste: teacherData.caste || null,
        rci_number: teacherData.rci_number?.trim() || null,
        rci_renewal_date: teacherData.rci_renewal_date || null,
        qualifications_details: teacherData.qualifications_details?.trim() || null,
        category: teacherData.category || null,
        email: teacherData.email?.trim() || null,
        class_assignments: classAssignment.class ? [classAssignment] : []
      };

      // Create teacher
      const token = localStorage.getItem('token');
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const teacherResponse = await axios.post(`${API_BASE_URL}/api/v1/teachers/`, teacherDataWithAssignments, config);
      const teacherId = teacherResponse.data.id;

      // Generate default password: Teacher + last 4 digits of effective Aadhaar
      const effectiveAadhaar = teacherDataWithAssignments.aadhar_number;
      const lastFourAadhaar = effectiveAadhaar.slice(-4);
      const generatedPassword = `Teacher${lastFourAadhaar}`;
      // Create user account only when a valid email is provided.
      let resolvedUsername = teacherData.email.split('@')[0];
      if (teacherData.email && teacherData.email.includes('@')) {
        try {
          const token = localStorage.getItem('token');
          const userRes = await axios.post(`${API_BASE_URL}/api/v1/users/`, {
            username: teacherData.email.split('@')[0],
            email: teacherData.email,
            password: generatedPassword,
            role: 'teacher',
            is_active: true,
            is_superuser: false
          }, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          // Capture the actual username the backend assigned
          // (may be "john_teacher" if "john" was already taken by another role)
          if (userRes.data?.username) {
            resolvedUsername = userRes.data.username;
          }
        } catch (userError) {
          console.warn('User creation warning:', userError);
          // Continue anyway - teacher profile was created
        }
      }

      setActualUsername(resolvedUsername);
      setDefaultPassword(generatedPassword);
      setShowSuccessModal(true);
      
      // Auto-redirect after 60 seconds (1 minute)
      setTimeout(() => {
        navigate('/headmaster');
      }, 60000);
    } catch (error) {
      console.error('Error adding teacher:', error);
      setIsSubmitting(false);
      showToast(error?.response?.data?.detail || 'Error adding teacher. Please try again.', 'error');
    }
  };

  const selectClassName = `w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all appearance-none text-[#6F6C90] bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23170F49%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.4-12.8z%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px_12px] bg-[center_right_1rem] bg-no-repeat pr-10`;

  const inputEditStyles = `
    .input-edit {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      background-color: #f9fafb;
      font-size: 14px;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .input-edit:focus {
      outline: none;
      border-color: #E38B52;
      box-shadow: 0 0 0 3px rgba(227, 139, 82, 0.1);
      background-color: white;
    }
    textarea.input-edit {
      resize: vertical;
      min-height: 120px;
    }

    /* Ensure checkbox checkmark/accent color matches Add Student (use accent-color) */
    .add-teacher-root input[type="checkbox"] {
      accent-color: #E38B52;
      -webkit-accent-color: #E38B52;
    }
  `;

  // inject styles to document head if not already injected
  if (typeof document !== 'undefined' && !document.getElementById('ssm-input-edit-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'ssm-input-edit-styles';
    styleElement.textContent = inputEditStyles;
    document.head.appendChild(styleElement);
  }

  return (
    <div className="add-teacher-root min-h-screen max-lg:min-h-[100dvh] w-full flex flex-col items-center justify-center bg-[#f7f7f7] relative overflow-hidden py-20 max-lg:py-0 max-lg:pb-[calc(40px+env(safe-area-inset-bottom))] max-lg:pt-0 max-lg:overflow-x-hidden">

      {/* Mobile Sticky Header */}
      <div className="hidden max-lg:flex sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 py-3 items-center shadow-sm">
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-[#6F6C90] hover:text-[#170F49] transition-colors bg-transparent p-0 m-0 border-none shadow-none"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="font-medium text-sm">Back</span>
        </button>
      </div>

      {/* Back button */}
      <button
        onClick={() => window.history.back()}
        className="absolute top-8 left-8 max-lg:hidden bg-white/30 backdrop-blur-xl rounded-2xl shadow-xl p-3 border border-white/20 hover:-translate-y-1 transition-all duration-200 flex items-center gap-2 z-10"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      {/* Animated background blobs */}
      <div className="absolute top-0 -left-40 w-[600px] h-[500px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-float z-0" />
      <div className="absolute -bottom-32 right-40 w-[600px] h-[600px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-40 animate-float animation-delay-3000 z-0" />
      <div className="absolute top-1/2 left-1/2 w-[500px] h-[500px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-40 animate-float animation-delay-5000 z-0" />
      <div className="absolute top-0 -left-40 w-[500px] h-[600px] bg-[#E38B52] rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-float animation-delay-7000 z-0" />
      
      <div className="w-[90%] max-w-[1200px] mx-4 flex-1 flex flex-col justify-center max-lg:w-[95%] max-lg:mx-auto max-lg:mt-4">
        <h1 className="text-3xl font-bold text-[#170F49] mb-8 max-md:mb-5 text-center font-baskervville">
          Add Teacher
        </h1>
        
        {/* Container with adjusted padding */}
        <div className="relative bg-white/30 backdrop-blur-xl rounded-3xl shadow-xl p-8 md:p-12 max-md:p-3 border border-white/20 max-w-[600px] mx-auto w-full">
          <form onSubmit={handleSubmit} className="space-y-6 max-md:space-y-4">
            <div className="space-y-2 w-full">
              <label className="block text-sm font-medium text-[#170F49] ml-4">
                Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={teacherData.name}
                onChange={handleInputChange}
                placeholder="Enter Name"
                className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all placeholder:text-[#6F6C90]"
              />
              {errors.name && (<p className="text-red-500 text-xs mt-1">{errors.name}</p>)}
            </div>

            <div className="space-y-2 w-full">
              <label className="block text-sm font-medium text-[#170F49] ml-4">
                Address
              </label>
              <textarea
                name="address"
                id="address"
                value={teacherData.address}
                onChange={handleInputChange}
                placeholder="Enter Address"
                className="input-edit"
              />
              {errors.address && (<p className="text-red-500 text-xs mt-1">{errors.address}</p>)}
            </div>

            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  Date of Birth
                </label>
                <input
                  type="text"
                  name="date_of_birth"
                  id="date_of_birth"
                  value={teacherData.date_of_birth}
                  onChange={handleInputChange}
                  onFocus={(e) => { e.target.type = 'date'; }}
                  onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                  placeholder="YYYY-MM-DD"
                  className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all text-[#6F6C90]"
                />
                {errors.date_of_birth && (<p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>)}
              </div>
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  Gender
                </label>
                <select
                  name="gender"
                  id="gender"
                  value={teacherData.gender}
                  onChange={handleInputChange}
                  className={selectClassName}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && (<p className="text-red-500 text-xs mt-1">{errors.gender}</p>)}
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  Blood Group
                </label>
                <select
                  name="blood_group"
                  id="blood_group"
                  value={teacherData.blood_group}
                  onChange={handleInputChange}
                  className={selectClassName}
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
                {errors.blood_group && (<p className="text-red-500 text-xs mt-1">{errors.blood_group}</p>)}
              </div>
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  name="mobile_number"
                  id="mobile_number"
                  value={teacherData.mobile_number}
                  onChange={handleInputChange}
                  placeholder="Enter Mobile Number"
                  className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all placeholder:text-[#6F6C90]"
                />
                {errors.mobile_number && (<p className="text-red-500 text-xs mt-1">{errors.mobile_number}</p>)}
              </div>
            </div>
            <div className="space-y-2 w-full">
              <label className="block text-sm font-medium text-[#170F49] ml-4">
                Email
              </label>
              <input
  type="email"
  name="email"
  id="email"
  value={teacherData.email}
  onChange={handleInputChange}
  placeholder="Enter Email"
  required
  className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all placeholder:text-[#6F6C90]"
  pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
  onInvalid={e =>
    e.target.setCustomValidity(
      'Please enter a valid email address (username@domain.extension) with no spaces.'
    )
  }
  onInput={e => e.target.setCustomValidity('')}
/>
              {errors.email && (<p className="text-red-500 text-xs mt-1">{errors.email}</p>)}
            </div>

            <div className="space-y-2 w-full">
              <label className="block text-sm font-medium text-[#170F49] ml-4">
                Aadhar Number
              </label>
              <input
                type="text"
                name="aadhar_number"
                id="aadhar_number"
                value={teacherData.aadhar_number}
                onChange={handleInputChange}
                inputMode="numeric"
                pattern="\d{4}\s?\d{4}\s?\d{4}"
                maxLength={14}
                placeholder="1234 5678 9012"
                className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1] transition-all placeholder:text-[#6F6C90]"
              />
              {aadharError && <p className="text-red-500 text-xs mt-1">{aadharError}</p>}
              {errors.aadhar_number && !aadharError && (<p className="text-red-500 text-xs mt-1">{errors.aadhar_number}</p>)}
            </div>

            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  Religion
                </label>
                <select
                  name="religion"
                  value={teacherData.religion}
                  onChange={handleInputChange}
                  className={selectClassName}
                >
                  <option value="">Select Religion</option>
                  <option value="Hindu">Hindu</option>
                  <option value="Christian">Christian</option>
                  <option value="Muslim">Muslim</option>
                  <option value="Sikh">Sikh</option>
                  <option value="Jew">Jew</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  Caste
                </label>
                <input
                  type="text"
                  name="caste"
                  value={teacherData.caste}
                  onChange={handleInputChange}
                  placeholder="Enter Caste"
                  className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all placeholder:text-[#6F6C90]"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  RCI Number
                </label>
                <input
                  type="text"
                  name="rci_number"
                  id="rci_number"
                  value={teacherData.rci_number}
                  onChange={handleInputChange}
                  placeholder="Enter RCI Number"
                  className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all placeholder:text-[#6F6C90]"
                />
                {errors.rci_number && (<p className="text-red-500 text-xs mt-1">{errors.rci_number}</p>)}
              </div>
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-[#170F49] ml-4">
                  RCI Renewal Date
                </label>
                <input
                  type="text"
                  name="rci_renewal_date"
                  id="rci_renewal_date"
                  value={teacherData.rci_renewal_date}
                  onChange={handleInputChange}
                  onFocus={(e) => { e.target.type = 'date'; }}
                  onBlur={(e) => { if (!e.target.value) e.target.type = 'text'; }}
                  placeholder="YYYY-MM-DD"
                  className="w-full px-4 py-4 rounded-2xl border bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all text-[#6F6C90]"
                />
                {errors.rci_renewal_date && (<p className="text-red-500 text-xs mt-1">{errors.rci_renewal_date}</p>)}
              </div>
            </div>

            <div className="space-y-2 w-full">
              <label className="block text-sm font-medium text-[#170F49] ml-4">
                Qualifications Details
              </label>
              <textarea
                name="qualifications_details"
                id="qualifications_details"
                value={teacherData.qualifications_details}
                onChange={handleInputChange}
                placeholder="Enter Qualifications Details"
                className="input-edit"
              />
              {errors.qualifications_details && (<p className="text-red-500 text-xs mt-1">{errors.qualifications_details}</p>)}
            </div>

            <div className="space-y-2 w-full">
              <label className="block text-sm font-medium text-[#170F49] ml-4">
                Category
              </label>
              <select
                name="category"
                id="category"
                value={teacherData.category}
                onChange={handleInputChange}
                className={selectClassName}
              >
                <option value="">Select Category</option>
                <option value="General">General</option>
                <option value="OBC">OBC</option>
                <option value="SC">SC</option>
                <option value="ST">ST</option>
                <option value="Other">Other</option>
              </select>
              {errors.category && (<p className="text-red-500 text-xs mt-1">{errors.category}</p>)}
            </div>

            {/* Class Assignment Section */}
            <div className="space-y-6 max-md:space-y-4 w-full">
              <h2 className="text-xl font-semibold text-[#170F49] border-b pb-2">
                Class Assignment
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-md:gap-4">
                <div className="space-y-2">
                  <div className="flex gap-3 items-end">
                    <div className="w-[70%]">
                      <label className="block text-sm font-medium text-[#170F49] ml-4 mb-2">
                        Class <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="class_assignment_class"
                        value={classAssignment.class}
                        onChange={(e) => handleClassAssignmentChange('class', e.target.value)}
                        className={selectClassName}
                      >
                        <option value="">Select Class</option>
                        <option value="PrePrimary">PrePrimary</option>
                        <option value="Primary 1">Primary 1</option>
                        <option value="Primary 2">Primary 2</option>
                        <option value="Secondary">Secondary</option>
                        <option value="Pre vocational 1">Pre vocational 1</option>
                        <option value="Pre vocational 2">Pre vocational 2</option>
                        <option value="Care group below 18 years">Care group below 18 years</option>
                        <option value="Care group Above 18 years">Care group Above 18 years</option>
                        <option value="Vocational 18-35 years">Vocational 18-35 years</option>
                      </select>
                    </div>
                    <div className="w-[30%]">
                      <label className="block text-sm font-medium text-[#170F49] mb-2">Division</label>
                      <select
                        id="class_assignment_division"
                        value={classAssignment.division}
                        onChange={(e) => handleClassAssignmentChange('division', e.target.value)}
                        className="w-[100%] px-3 py-3 rounded-xl border bg-white shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#E38B52] transition-all duration-300 text-[#6F6C90]"
                        disabled={!classAssignment.class}
                      >
                        <option value="">Select</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#170F49] ml-4">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="class_assignment_year"
                    value={classAssignment.year}
                    onChange={(e) => handleClassAssignmentChange('year', e.target.value)}
                    className={selectClassName}
                  >
                    <option value={new Date().getFullYear().toString()}>{new Date().getFullYear()}</option>
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i - 1;
                      return <option key={year} value={year.toString()}>{year}</option>
                    })}
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() + i + 1;
                      return <option key={year} value={year.toString()}>{year}</option>
                    })}
                  </select>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#E38B52] text-white py-4 rounded-2xl hover:bg-[#C8742F] hover:-translate-y-1 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed
              shadow-[inset_0_2px_4px_rgba(255,255,255,0.3),inset_0_4px_8px_rgba(255,255,255,0.2)]"
            >
              {isSubmitting ? 'Adding Teacher...' : 'Add Teacher'}
            </button>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[#170F49] mb-3">Teacher Added Successfully!</h3>
              <p className="text-gray-600 mb-4">A login account has been created for the teacher.</p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
                <p className="text-sm font-medium text-[#170F49] mb-2">🔑 Teacher Login Credentials:</p>
                <p className="text-sm text-gray-700 mb-1">
                  <span className="font-medium">Username:</span>{' '}
                  <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">
                    {actualUsername || teacherData.email.split('@')[0]}
                  </code>
                </p>
                <p className="text-sm text-gray-700 mb-2">
                  <span className="font-medium">Password:</span>{' '}
                  <code className="bg-white px-2 py-1 rounded border border-gray-300 font-mono">{defaultPassword}</code>
                </p>
                <p className="text-xs text-gray-500">The teacher can change this password after login.</p>
                {actualUsername && actualUsername !== teacherData.email.split('@')[0] && (
                  <p className="text-xs text-amber-600 mt-2">
                    ℹ️ The email prefix was already used by another account, so a unique username was assigned.
                  </p>
                )}
              </div>

              <p className="text-sm text-gray-500 mb-4">Redirecting to HeadMaster in 60 seconds...</p>
              
              <button
                onClick={() => navigate('/headmaster')}
                className="w-full px-6 py-2 bg-[#E38B52] text-white rounded-lg hover:bg-[#C8742F] font-medium transition-all"
              >
                Go to HeadMaster Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global styles for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(100px, -100px) scale(1.2);
          }
          50% {
            transform: translate(0, 100px) scale(0.9);
          }
          75% {
            transform: translate(-100px, -50px) scale(1.1);
          }
          100% {
            transform: translate(0, 0) scale(1);
          }
        }
        .animate-float {
          animation: float 15s infinite ease-in-out;
        }
        .animation-delay-3000 {
          animation-delay: -5s;
        }
        .animation-delay-5000 {
          animation-delay: -10s;
        }
        .animation-delay-7000 {
          animation-delay: -15s;
        }
      `}</style>

      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-8 right-8 z-[9999] animate-slide-in-right ${
            toast.type === "success"
              ? "bg-green-500"
              : toast.type === "error"
              ? "bg-red-500"
              : "bg-blue-500"
          } text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 min-w-[320px] max-w-md`}
        >
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
            .animate-slide-in-right {
              animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            }
          `}</style>
          <div className="flex-shrink-0">
            {toast.type === "success" ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <div className="flex-grow font-semibold text-sm tracking-wide">
            {toast.message}
          </div>
          <button
            onClick={() => setToast({ show: false, message: "", type: "" })}
            className="flex-shrink-0 text-xl font-bold hover:text-white/80 transition-colors cursor-pointer"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default AddTeacher;












